import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { Chip } from "@/components/badge/chip";
import { UserChip } from "@/components/user-chip";
import { useApps } from "@/hooks/apps";
import { useUserMap } from "@/hooks/users";
import { useTabParam } from "@/hooks/tab-param";
import { versionLabel } from "@/lib/version";
import type { Prd } from "@/types/domain";
import { DATE_FULL } from "@/lib/date";
import { CompletionProgress } from "@/components/engagements/completion-progress";
import { prdDetailPath } from "@/lib/paths";

// Bug issues (prds with type=bug), scoped to a version (sourceVersionId),
// a whole app (appId) or an assignee (ownerId — "My bugs"). Rows show
// provenance badges: source version (outside a version page) and source PRD
// when filed against a PRD within a version. Click-through opens the issue
// engagement detail (/e/:id).
export const BugsList = ({
  versionId,
  pid,
  ownerId,
  breadcrumb,
}: {
  versionId?: string;
  pid?: string;
  ownerId?: string;
  /** Origin crumbs for the detail page (e.g. My bugs → back to /bugs). */
  breadcrumb?: { label?: string; to?: string }[];
}) => {
  const navigate = useNavigate();
  const apps = useApps();
  const users = useUserMap();
  const [status, setStatus] = useTabParam("bugs", "all");
  // Fetch all states once (bug volume is tiny); tabs filter client-side.
  const { data, isLoading } = useSWR<{ data: Prd[]; total: number }>(
    swrKey("/prds", {
      type: "bug",
      pageSize: 100,
      ...(versionId
        ? { sourceVersionId: versionId }
        : pid
        ? { appId: pid }
        : { ownerId }),
    }),
    // Switching version / app keeps the previous bugs on screen while refetching.
    { keepPreviousData: true }
  );
  const bugs: Prd[] = data?.data ?? [];
  const isMine = (b: Prd) => !!b.canComplete || !!b.canRemind;
  const openCount = bugs.filter(b => isMine(b) && !b.myCompletedAt).length;
  const doneCount = bugs.filter(b => isMine(b) && !!b.myCompletedAt).length;
  const filtered =
    status === "open" ? bugs.filter(b => isMine(b) && !b.myCompletedAt)
      : status === "done" ? bugs.filter(b => isMine(b) && !!b.myCompletedAt) : bugs;

  if (bugs.length === 0) {
    return isLoading && !data ? <ListLoading /> : <ListEmpty>No bugs.</ListEmpty>;
  }

  return (
    <div className="flex flex-col gap-2">
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="open">
            My Open ({openCount})
          </TabsTrigger>
          <TabsTrigger value="done">
            My Done ({doneCount})
          </TabsTrigger>
          <TabsTrigger value="all">
            All ({bugs.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {filtered.length === 0 && <ListEmpty>No {status} bugs.</ListEmpty>}
      <div className="flex flex-col gap-1">
        {filtered.map((bug, i) => (
          <RowCard key={bug.engagementId ?? bug.id} onClick={() => navigate(prdDetailPath(bug.id, bug.engagementId), { state: { breadcrumb } })}>
            <RowIndex n={i + 1} />
            {bug.engagements?.[0] && <CompletionProgress engagement={bug.engagements[0]} spin />}
            <Chip>{apps.find(a => String(a.id) === bug.appId)?.name ?? bug.appId}</Chip>
            <span className="min-w-0 flex-1 truncate">{bug.docName}</span>
            {!bug.myCompletedAt && bug.myStartedAt && <Chip tone="yellow">In Process</Chip>}
            {bug.sourcePrd && (
              <Chip tone="purple" className="max-w-40 truncate">
                {bug.sourcePrd.docName}
              </Chip>
            )}
            {/* Source-version chip only outside a version page */}
            {!versionId && bug.sourceVersion && (
              <Link
                to={`/apps/${bug.sourceVersion.pid}/versions/${bug.sourceVersion.id}`}
                className="shrink-0"
              >
                <Chip tone="blue" className="hover:underline">
                  {versionLabel(bug.sourceVersion, apps)}
                </Chip>
              </Link>
            )}
            {/* Owner chip is redundant in the "my bugs" (ownerId) view */}
            {!ownerId && bug.ownerId && users.get(bug.ownerId) && (
              <UserChip user={users.get(bug.ownerId)!} className="shrink-0" />
            )}
            <UserChip
              user={
                (bug.creatorId && users.get(bug.creatorId)) || {
                  name: bug.creatorName ?? undefined,
                }
              }
              className="w-20 shrink-0"
            />
            <span className="shrink-0 text-xs text-muted-foreground">
              {format(new Date(bug.createdAt ?? 0), DATE_FULL)}
            </span>
          </RowCard>
        ))}
      </div>
    </div>
  );
};

export default BugsList;
